import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CRow,
} from '@coreui/react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { setRuleHaveEditting } from '../../../../../../actions/common';
import { reloadUserSource } from '../../../../../../actions/subscriber';
import { callTokenApi } from '../../../../../../apiCaller';
import { API_CLIENT_RULE_UPDATE, API_CLIENT_RULES, TYPE_SHOW_UNSAVE_CHANGE } from '../../../../../../constants';
import { useActiveListener } from '../../../../../../helpers/customHooks';
import { toastError } from '../../../../../../utils';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { ConfirmSaveChange } from '../../../../../general/popup';
import { RuleContext } from '../EditLookupCustom';
import { CInput, CTextarea } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import CLabel from '../../../../../migration/CLabel';
import CFormGroup from '../../../../../migration/CFormGroup';

const EditStep2 = ({ stepsData, setStepsData }) => {
    const dispatch = useDispatch();
    const { activeStep, setActiveStep, setRule } = useContext(RuleContext);
    const [saveChangeModal, setSaveChangeModal] = useState(false);
    const [tempData, setTempData] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const activeAccount = useSelector(state => state.subscriber.activeAccount);
    const activeListener = useActiveListener();
    const initialValues = {
        name: stepsData.name,
        description: stepsData.description
    }
    const [userSourceRules, setUserSourceRules] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(true);

    const fetchRules = () => {
        if (userSourceRules.length === 0 && activeStep === 2) {
            let data = {
                accountId: activeAccount.id,
                listenerId: activeListener.id,
                group: 'userSource'
            };
            callTokenApi(`${API_CLIENT_RULES}`, 'POST', data)
                .then(response => {
                    if (response.status === 200) {
                        const { rules } = response.data;
                        setUserSourceRules(rules);
                        setFetchLoading(false);
                    } else {
                        toastError(response);
                    }
                })
        }
    }

    useEffect(fetchRules, [activeStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const validationSchema = useCallback(() => {
        let ruleNames = userSourceRules.map(rule => rule.name);

        if (stepsData.name) {
            let ruleNameIndex = -1;

            ruleNames.some((ruleName, index) => {
                if (stepsData.name === ruleName) {
                    ruleNameIndex = index;
                    return true;
                }

                return false;
            })

            if (ruleNameIndex !== -1) {
                ruleNames.splice(ruleNameIndex, 1);
            }
        }

        return (
            Yup.object().shape({
                name: Yup.string()
                    .required('Name is required')
                    .max(200, 'Name cannot be more than 200 characters')
                    .notOneOf(ruleNames, 'Existed name, please choose another name')
                ,
            })
        )
    }, [userSourceRules, stepsData.name])

    const onSubmit = ({ name, description }, { setSubmitting }) => {
        setSubmitting(false);
        const data = {
            name: name.trim(),
            description: description.trim()
        }

        if (JSON.stringify(initialValues) === JSON.stringify(data)) {
            setActiveStep(3);
        } else {
            setTempData(data);
            setSaveChangeModal(true);
        }
    }

    const onAccept = () => {
        setSaveLoading(true);
        const newStepsData = { ...stepsData, ...tempData };

        callTokenApi(API_CLIENT_RULE_UPDATE, 'POST', newStepsData)
            .then((response) => {
                setSaveLoading(false);
                setSaveChangeModal(false);

                if (response.status === 200) {
                    setRule(newStepsData);
                    setStepsData(newStepsData);
                    dispatch(reloadUserSource());
                    toast.success('Successfully update rule');
                    setActiveStep(3);
                } else {
                    toastError(response);
                }
            })
    };

    const handleHasChange = () => {
        dispatch(setRuleHaveEditting({ showLv2: true, typeLv2: TYPE_SHOW_UNSAVE_CHANGE.EDIT_LOOKUP }));
    };

    return (
        <CCard className={`cvr-step-2 ${activeStep !== 2 ? 'difference-step' : 'cvr-step-card'}`}>
            <CCardHeader>
                {
                    activeStep !== 2 ? (
                        <div className="rule-step d-inline-flex justify-content-between w-100">
                            <h5 className="mb-0 inactive">Step 1: Rule Name & Description</h5>
                            {
                                stepsData.name && (
                                    <div className="d-inline-flex align-items-center">
                                        <span className="given-data mr-3">{stepsData.name}</span>
                                        <CButton className="btn-edit" onClick={() => setActiveStep(2)}>
                                            Edit
                                        </CButton>
                                    </div>
                                )
                            }
                        </div>
                    ) : (
                        <h5 className="mb-0 inactive">Step 1: Rule Name & Description</h5>
                    )
                }
            </CCardHeader>
            {
                activeStep === 2 && (
                    <CCardBody>
                        {
                            fetchLoading ? (
                                <CenterSpinner />
                            ) : (
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={onSubmit}
                                    validateOnChange={false}
                                    validateOnBlur={false}
                                >
                                    {
                                        ({
                                            values,
                                            errors,
                                            handleChange,
                                            handleSubmit,
                                            setErrors,
                                            isSubmitting,
                                        }) => (
                                            <CForm onSubmit={handleSubmit} noValidate>
                                                <CRow>
                                                    <CCol md='7'>
                                                        <CFormGroup>
                                                            <CLabel htmlFor="name">Name this rule set</CLabel>
                                                            <CInput
                                                                id="name"
                                                                placeholder="Rule set name"
                                                                invalid={!!errors.name}
                                                                disabled={isSubmitting}
                                                                value={values.name}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    if (errors) {
                                                                        setErrors({});
                                                                    }
                                                                    handleHasChange();
                                                                }}
                                                                data-lpignore="true"
                                                            />
                                                            <CInvalidFeedback>{errors.name}</CInvalidFeedback>
                                                        </CFormGroup>
                                                    </CCol>
                                                    <CCol md='12'>
                                                        <CFormGroup >
                                                            <CLabel htmlFor="description">Describe this rule set</CLabel>
                                                            <CTextarea
                                                                id="description"
                                                                placeholder="Enter a description for your own reference..."
                                                                disabled={isSubmitting}
                                                                value={values.description}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    handleHasChange();
                                                                }}
                                                                data-lpignore="true"
                                                            />
                                                        </CFormGroup>
                                                    </CCol>
                                                </CRow>
                                                <CButton
                                                    type="submit"
                                                    className="px-4"
                                                    color="primary"
                                                >
                                                    {`SAVE & NEXT`}
                                                </CButton>
                                            </CForm>
                                        )
                                    }
                                </Formik>
                            )
                        }
                    </CCardBody>
                )
            }
            <ConfirmSaveChange
                show={saveChangeModal}
                onClose={() => setSaveChangeModal(false)}
                onAccept={onAccept}
                title="Are You Sure You Want to Save This Change?"
                isLoading={saveLoading}
            >
                You are about to update the name/description of this rule. This will not effect any other rule settings.
            </ConfirmSaveChange>
        </CCard>
    )
}

EditStep2.propTypes = {
    stepsData: PropTypes.object,
    setStepsData: PropTypes.func
}

export default EditStep2
